import api from './api'

// Ids are typed `number | string` and are interpolated into request URLs, so
// they are encoded first: a value containing '/', '?' or '../' would otherwise
// rewrite the path and hit an unintended endpoint. No-op for numeric ids.
const segment = (id: number | string): string => encodeURIComponent(String(id))

export const productsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/products', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/products/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/products', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/products/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/products/${segment(id)}`),
  getForEdit: (id: number | string): Promise<any> => api.get(`/products/${segment(id)}/edit`),
  getCreateData: (): Promise<any> => api.get('/products/create'),
  getDetail: (id: number | string): Promise<any> => api.get(`get_product_detail/${segment(id)}`),
  importCsv: (formData: FormData): Promise<any> => api.post('products/import/csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getTransactions: (id: number | string, params: Record<string, any> = {}): Promise<any> => api.get(`get_product_transactions/${segment(id)}`, { params }),
  getWarehouses: (id: number | string): Promise<any> => api.get(`get_product_warehouses/${segment(id)}`),
  getUnits: (id: number | string): Promise<any> => api.get(`get_product_units/${segment(id)}`),
  getProductData: (productId: number | string, warehouseId: number | string): Promise<any> => api.get(`show_product_data/${segment(productId)}/${segment(warehouseId)}`),
  getByWarehouse: (warehouseId: number | string, params: Record<string, any> = {}): Promise<any> => api.get(`get_products_by_warehouse/${segment(warehouseId)}`, { params }),
}

export const categoriesApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/categories', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/categories/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/categories', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/categories/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/categories/${segment(id)}`),
  deleteMultiple: (selectedIds: (number | string)[]): Promise<any> => api.post('/categories_delete_by_selection', { selectedIds }),
}

export const brandsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/brands', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/brands/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/brands', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/brands/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/brands/${segment(id)}`),
  deleteMultiple: (selectedIds: (number | string)[]): Promise<any> => api.post('/brands_delete_by_selection', { selectedIds }),
}

export const unitsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/units', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/units/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/units', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/units/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/units/${segment(id)}`),
  deleteMultiple: (selectedIds: (number | string)[]): Promise<any> => api.post('/units_delete_by_selection', { selectedIds }),
  getByProduct: (productId: number | string): Promise<any> => api.get(`get_units?id=${segment(productId)}`),
}

// The four API objects define identical method names (getAll, create, ...),
// so a flat spread made every method silently resolve to the LAST object
// (units) — categories/brands/products were unreachable. Namespace them so
// each domain stays addressable.
export const productsCombinedApi = {
  products: productsApi,
  categories: categoriesApi,
  brands: brandsApi,
  units: unitsApi,
}

export default productsApi
